import { ChartDataSets } from "chart.js"

type HexColor = `#${string}`

type StandardTimeMeasurementLinesValues = {
  max: number,
  average: number,
  theoreticalTime: number
}

type TimeMeasurementLinesOptions = {
  taktTime?: number
  targetTime?: number
  datasets?: StandardTimeMeasurementLinesValues[][]
}

type ChartElement = {
  getPixelForValue(value: number): number,
  width: number,
  ticks: {
    length: number
  }
}

enum LineColors {
  theoreticalTime = '#71D2FF',
  average = '#FFBA2C',
  max = '#CA93FF',
  taktTime = '#E0322D',
  targetTime = '#3A8800'
}

type ChartScales = {
  scales: {
    'x-axis-0': ChartElement,
    'y-axis-0': ChartElement
  }
}

const TimeMeasurementLines : Chart.PluginServiceRegistrationOptions & Chart.PluginServiceGlobalRegistration = {
  id: 'timeMeasurementLines',
  beforeInit (chart, options) {
    const {ctx, data: {datasets}} = chart
    const ticksConfig = chart.options.scales?.yAxes?.[0]?.ticks
    if (!ctx || !datasets || !ticksConfig) {
      return
    }

    ctx.save()

    const absoluteMaxValue = getAbsoluteMaxValue(datasets, options)
    const configuredSuggestedMax = ticksConfig.suggestedMax
    if (configuredSuggestedMax === undefined || configuredSuggestedMax < absoluteMaxValue) {
      ticksConfig.suggestedMax = absoluteMaxValue
      ctx.save()
    }
  },
  afterDatasetsDraw (chart: Chart & ChartScales, args, options: TimeMeasurementLinesOptions) {
    const { ctx } = chart
    if (!ctx || !chart) {
      return
    }

    ctx.save()

    drawDatasetsRectsFromMaxValuesToMinRepeatableValues({chart, options})
    drawThresholdsLines({chart, options})
    drawDatasetsTimeMeasurementValuesLines({chart, options})
  },
}

function getAbsoluteMaxValue (datasets: ChartDataSets[] , options: TimeMeasurementLinesOptions): number {
  const allValues: number[] = []
  for (const dataset of datasets) {
    if (!Array.isArray(dataset.data) || !isNumberArray(dataset.data)) {
      continue
    }
    allValues.push(...(dataset.data ?? []))
  }

  for (const timeMeasurementDatasetConfig of options.datasets ?? []) {
    for (const timeMeasurementData of timeMeasurementDatasetConfig) {
      allValues.push(...Object.values(timeMeasurementData ?? {}))
    }
  }

  if (options.taktTime) {
    allValues.push(options.taktTime)
  }

  if (options.targetTime) {
    allValues.push(options.targetTime)
  }

  return Math.max(...allValues)
}

function drawDatasetsRectsFromMaxValuesToMinRepeatableValues ({chart, options}: {chart: Chart & ChartScales, options: TimeMeasurementLinesOptions}) {
  const {ctx, data: {datasets}, scales: {'y-axis-0': yAxis0}} = chart
  if (!ctx || !datasets) {
    return
  }

  const barWidthInPx = getBarWidthInPx(chart)
  forValueInDatasets({datasets, options}, ({valueIdx, datasetIdx, dataValue, timeMeasurementData: {max}}) => {
    if (max > dataValue) {
      ctx.fillStyle = '#DFF1F7'
      const pixelForMaxValue = yAxis0.getPixelForValue(max)
      const pixelForDataValue = yAxis0.getPixelForValue(dataValue)
      const standardXAxisPositionPixel = getBarXPosition({datasetIndex: datasetIdx, valueIndex: valueIdx, chart})
      ctx.fillRect(standardXAxisPositionPixel - (barWidthInPx/2), pixelForMaxValue, barWidthInPx, -(pixelForMaxValue - pixelForDataValue))
    }
  })
}

function getBarWidthInPx (chart: Chart & ChartScales) : number {
  const {scales: {'x-axis-0': xAxis0}} = chart
  const chartDatasets = chart.data.datasets ?? []
  if (chartDatasets.length === 0) {
    return 0
  }

  const dataset = chartDatasets[0] ?? {}

  const barThickness = dataset.barThickness ?? dataset.maxBarThickness

  if (barThickness === 'flex') {
    throw new Error("TimeMeasurementLines cannot perform operations with Flex Bar Thickness")
  }

  if (typeof barThickness === 'number') {
    return barThickness
  }

  const width = xAxis0.width
  const numberOfDivisions = xAxis0.ticks.length
  const {barPercentage= 0.9, categoryPercentage = 0.8} = dataset
  return ((width / numberOfDivisions) * barPercentage * categoryPercentage) / chartDatasets.length
}

function getBarXPosition ({datasetIndex, valueIndex, chart}: {datasetIndex: number, valueIndex: number, chart: Chart}): number {
  const datasetMeta = chart.getDatasetMeta(datasetIndex)
  return datasetMeta.data?.[valueIndex]?._view.x ?? 0
}

function forValueInDatasets (
  {datasets, options}: {datasets: ChartDataSets[], options: TimeMeasurementLinesOptions},
  cb: ({valueIdx, datasetIdx, dataValue, timeMeasurementData} : {valueIdx: number, datasetIdx: number, dataValue: number, timeMeasurementData: StandardTimeMeasurementLinesValues}) => void
) {
  for (let datasetIdx = 0; datasetIdx < datasets.length; datasetIdx++) {
    const datasetData = datasets[datasetIdx]?.data ?? []
    const optionsDataset = options.datasets?.[datasetIdx]
    if (!datasetData || !optionsDataset) {
      continue
    }
    for (let valueIdx = 0; valueIdx < datasetData.length; valueIdx++) {
      const dataValue = datasetData[valueIdx]
      if (typeof dataValue !== 'number') {
        continue
      }

      const {max=0, average= 0, theoreticalTime= 0} = optionsDataset[valueIdx] ?? {}
      cb({datasetIdx, valueIdx, dataValue, timeMeasurementData: {max, average, theoreticalTime}})
    }
  }
}

function drawDatasetsTimeMeasurementValuesLines ({chart, options}: {chart: Chart & ChartScales, options: TimeMeasurementLinesOptions}) {
  const {data: {datasets}} = chart
  if (!datasets) {
    return
  }

  forValueInDatasets({datasets, options}, ({valueIdx, datasetIdx, timeMeasurementData: {max, average, theoreticalTime}}) => {
    drawLineForDataset({chart, color: LineColors.theoreticalTime, xValue: valueIdx, datasetIdx, yValue: theoreticalTime})
    drawLineForDataset({chart, color: LineColors.max, xValue: valueIdx, datasetIdx, yValue: max})
    drawLineForDataset({chart, color: LineColors.average, xValue: valueIdx, datasetIdx, yValue: average})
  })
}

function drawLineForDataset ({chart, xValue, datasetIdx, yValue, color}: {chart: Chart & ChartScales, xValue: number, datasetIdx: number, yValue: number, color: HexColor}) {
  const {ctx, data: {datasets}, scales: {'y-axis-0': yAxis}} = chart
  if (!ctx || !datasets || yValue === 0) {
    return
  }

  const startPoint = getBarXPosition({datasetIndex: datasetIdx, valueIndex: xValue, chart})
  const barWidthInPx = getBarWidthInPx(chart)
  const lineHeightInPx = 2
  const valueCoordinateY = yAxis.getPixelForValue(yValue)

  ctx.fillStyle = color
  ctx.fillRect(startPoint - (barWidthInPx/2), valueCoordinateY, barWidthInPx, lineHeightInPx)
}

function drawThresholdsLines ({chart, options}: {chart: Chart & ChartScales, options: TimeMeasurementLinesOptions}) {
  if (options.targetTime) {
    drawFullChartWidthHorizontalLine({
      chart,
      yAxisValue: options.targetTime,
      color: LineColors.targetTime,
    })
  }
  if (options.taktTime) {
    drawFullChartWidthHorizontalLine({
      chart,
      yAxisValue: options.taktTime,
      color: LineColors.taktTime,
      style: "dashed",
    })
  }

}

function drawFullChartWidthHorizontalLine ({chart, yAxisValue, color, style='strike'} : {
  chart: Chart & ChartScales,
  yAxisValue: number,
  color: HexColor,
  style?: 'strike' | 'dashed'
}) {
  const {
    ctx,
    scales: {'y-axis-0': yAxis, 'x-axis-0': xAxis},
    chartArea: {left: startOfGraph},
  } = chart

  if (!ctx) {
    return
  }

  const lineHeightInPx = 2
  const yPosition = yAxis.getPixelForValue(yAxisValue)

  ctx.fillStyle = color

  if (style === 'strike') {
    ctx.fillRect(startOfGraph, yPosition, xAxis.width, lineHeightInPx)
  } else {
    let startPixel = startOfGraph
    const dashWidth = 4
    const dashGap= 6
    while(startPixel < xAxis.width) {
      ctx.fillRect(startPixel, yPosition, dashWidth, lineHeightInPx)
      startPixel += dashWidth + dashGap
    }
  }
}

function isNumberArray (input: unknown[]): input is number[] {
  return input.every(x => typeof x === 'number')
}


export default TimeMeasurementLines
